import { Component, Input } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, of, switchMap, takeUntil, timer } from 'rxjs';
import { ImageService } from '../../../../core/services/image.service';
import { FcmService } from '../../../../core/services/notification/fcm.service';
import { LanguageService } from '../../../../core/services/language/language.service';
import { ToastService } from '../../../../layout/toast/toast.service';
import { environment } from '../../../../../environments/environment';
import { setSessionAuthToken } from '../../../../core/auth/auth-token';

@Component({
  selector: 'app-login-component',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './login-component.html',
  styleUrl: './login-component.scss'
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() pageLabel = '';
  @Input() heroTitle = '';
  @Input() heroSubtitle = '';
  @Input() homeLink = '/';
  @Input() homeLinkText = '';
  @Input() panelLabel = '';
  @Input() panelTitle = '';
  @Input() panelCopy = '';
  @Input() authMode: 'default' | 'admin' = 'default';
  @Input() allowSignUp = true;

  @ViewChild('searchContainer') searchContainer!: ElementRef<HTMLDivElement>;

  public readonly languageService = inject(LanguageService);
  private readonly toast = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor(
    private readonly http: HttpClient,
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly fcmService: FcmService
  ) { }

  isLoginMode: boolean = true;
  showPassword: boolean = false;
  email = '';
  password = '';
  username = '';
  isCheckingUsername = false;
  usernameStatus: 'idle' | 'checking' | 'available' | 'taken' | 'invalid' = 'idle';
  usernameFeedback = '';
  private readonly usernameCheckSubject = new Subject<string>();
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  backendUrl = environment.backendUrl;
  frontendUrl = environment.frontendUrl;
  private filmIds: { type: string, id: number }[] = [
    { type: "movie", id: 278 },
    { type: "movie", id: 45612 },
    { type: "movie", id: 424 },
    { type: "movie", id: 106646 },
    { type: "movie", id: 155 },
    { type: "movie", id: 242582 },
    { type: "movie", id: 787 },
    { type: "movie", id: 27576 },
    { type: "movie", id: 19869 },
    { type: "movie", id: 121856 },
    { type: "movie", id: 4922 },
    { type: "movie", id: 72190 },
    { type: "movie", id: 49521 },
    { type: "movie", id: 209112 },
    { type: "movie", id: 329865 },
    { type: "movie", id: 274870 },
    { type: "movie", id: 324552 },
    { type: "movie", id: 263115 },
    { type: "movie", id: 283995 },
    { type: "movie", id: 315635 },
    { type: "movie", id: 284053 },
    { type: "movie", id: 284054 },
    { type: "movie", id: 299536 },
    { type: "movie", id: 533535 },
    { type: "movie", id: 438631 },
    { type: "movie", id: 395992 },
    { type: "movie", id: 353081 },
    { type: "movie", id: 4348 },
    { type: "movie", id: 624860 },
    { type: "movie", id: 351286 },
    { type: "movie", id: 269149 },
    { type: "movie", id: 109445 },
    { type: "movie", id: 1022789 },
    { type: "movie", id: 14160 },
    { type: "movie", id: 808 },
    { type: "movie", id: 396243 },
    { type: "movie", id: 122 },
    { type: "movie", id: 76600 },
    { type: "movie", id: 324786 },
    { type: "movie", id: 4538 },
    { type: "tv", id: 132117 },
    { type: "tv", id: 66732 },
    { type: "tv", id: 1399 },
    { type: "movie", id: 58 },
    { type: "tv", id: 78191 },
    { type: "movie", id: 19849 },
    { type: "movie", id: 976893 },
    { type: "movie", id: 673 },
    { type: "movie", id: 18785 },
    { type: "movie", id: 121856 },
    { type: "movie", id: 399566 }
  ]

  private destroy$ = new Subject<void>();
  private imageService = inject(ImageService);
  private index = 0;

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.usernameCheckSubject
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap((rawUsername) => {
          const cleaned = rawUsername.trim();
          if (!cleaned) {
            this.usernameStatus = 'idle';
            this.usernameFeedback = '';
            this.isCheckingUsername = false;
            return of(null);
          }
          if (cleaned.length < 3) {
            this.usernameStatus = 'invalid';
            this.usernameFeedback = 'Username must be at least 3 characters.';
            this.isCheckingUsername = false;
            return of(null);
          }
          if (cleaned.length > 30) {
            this.usernameStatus = 'invalid';
            this.usernameFeedback = 'Username cannot exceed 30 characters.';
            this.isCheckingUsername = false;
            return of(null);
          }
          if (!/^[a-zA-Z0-9_.-]+$/.test(cleaned)) {
            this.usernameStatus = 'invalid';
            this.usernameFeedback = 'Only letters, numbers, underscores, dots, and hyphens are allowed.';
            this.isCheckingUsername = false;
            return of(null);
          }

          this.isCheckingUsername = true;
          this.usernameStatus = 'checking';
          return this.http
            .get<{ available: boolean; message: string }>(
              `${this.backendUrl}/auth/check-username?username=${encodeURIComponent(cleaned)}`
            )
            .pipe(
              catchError(() =>
                of({ available: false, message: 'Could not verify username uniqueness.' })
              )
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.isCheckingUsername = false;
        if (!res) {
          return;
        }
        if (res.available) {
          this.usernameStatus = 'available';
          this.usernameFeedback = res.message || 'Username is available.';
        } else {
          this.usernameStatus = 'taken';
          this.usernameFeedback = res.message || 'This username is already taken.';
        }
      });
  }

  onUsernameChange(value: string): void {
    this.username = value;
    const cleaned = value.trim();
    if (!cleaned) {
      this.usernameStatus = 'idle';
      this.usernameFeedback = '';
      this.isCheckingUsername = false;
      return;
    }
    this.usernameStatus = 'checking';
    this.usernameCheckSubject.next(cleaned);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || !this.searchContainer) {
      return;
    }

    timer(0, 10000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const current = this.filmIds[this.index];

          this.index = (this.index + 1) % this.filmIds.length;

          return this.imageService.getImagePath(current.id, current.type).pipe(
            catchError(() => of(null))
          );
        })
      )
      .subscribe((imagePath) => {
        if (imagePath) {
          const container = this.searchContainer.nativeElement;
          container.style.background = `url(https://image.tmdb.org/t/p/w1280${imagePath})`;
          container.style.backgroundSize = "cover";
          container.style.backgroundPosition = "top";
        } else {
          this.searchContainer.nativeElement.style.background = '';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  get resolvedPageLabel(): string {
    if (this.pageLabel && this.pageLabel !== 'Member Access' && this.pageLabel !== 'Movie Lovers' && this.pageLabel !== 'Restricted Access') {
      return this.pageLabel;
    }
    return this.isAdminMode ? this.languageService.t('restrictedAccess') : this.languageService.t('movieLovers');
  }

  get resolvedHeroTitle(): string {
    if (this.heroTitle && this.heroTitle !== 'Welcome to The Cinema' && this.heroTitle !== 'Welcome to Scenetale' && this.heroTitle !== 'Scenetale Admin') {
      return this.heroTitle;
    }
    return this.isAdminMode ? this.languageService.t('adminLoginTitle') : this.languageService.t('welcomeToScenetale');
  }

  get resolvedHeroSubtitle(): string {
    if (this.heroSubtitle && this.heroSubtitle !== 'Lights. Camera. Emotion. Dive into the world of cinematic wonder.' && this.heroSubtitle !== 'Sign in to save favorites, track your watchlist, and keep your cinema world in sync.' && !this.heroSubtitle.startsWith('Editorial tools')) {
      return this.heroSubtitle;
    }
    return this.isAdminMode ? this.languageService.t('adminLoginSubtitle') : this.languageService.t('authSubtitle');
  }

  get resolvedHomeLinkText(): string {
    if (this.homeLinkText && this.homeLinkText !== 'Go to The Cinema' && this.homeLinkText !== 'Back to Home' && this.homeLinkText !== 'Return to Site') {
      return this.homeLinkText;
    }
    return this.isAdminMode ? this.languageService.t('returnToSite') : this.languageService.t('backToHome');
  }

  get resolvedPanelLabel(): string {
    if (this.panelLabel && this.panelLabel !== 'Access your account' && this.panelLabel !== 'Member Login' && this.panelLabel !== 'Admin Console') {
      return this.panelLabel;
    }
    if (this.isAdminMode) return this.languageService.t('adminConsole');
    return this.isLoginMode ? this.languageService.t('memberLogin') : this.languageService.t('memberSignUp');
  }

  get resolvedPanelTitle(): string {
    if (this.panelTitle && this.panelTitle !== 'Sign in to Scenetale' && this.panelTitle !== 'Sign in to manage the platform') {
      return this.panelTitle;
    }
    if (this.isAdminMode) return this.languageService.t('adminConsoleTitle');
    return this.isLoginMode ? this.languageService.t('pickUpWhereLeftOff') : this.languageService.t('createYourAccount');
  }

  get resolvedPanelCopy(): string {
    if (this.panelCopy && !this.panelCopy.startsWith('Use your preferred') && !this.panelCopy.startsWith('Use one of') && !this.panelCopy.startsWith('Use your authorized')) {
      return this.panelCopy;
    }
    if (this.isAdminMode) return this.languageService.t('adminConsoleCopy');
    return this.isLoginMode ? this.languageService.t('authCopyLogin') : this.languageService.t('authCopySignup');
  }

  changeMode() {
    if (!this.allowSignUp) {
      return;
    }
    this.isLoginMode = !this.isLoginMode;
    this.errorMessage = '';
    this.successMessage = '';
    this.usernameStatus = 'idle';
    this.usernameFeedback = '';
    this.isCheckingUsername = false;
  }

  async login(provider: string) {
    if (!this.isBrowser || this.isAdminMode) {
      return;
    }

    const providerPath = provider === 'google'
      ? 'google'
      : provider === 'github'
        ? 'github'
        : (provider === 'twitter' || provider === 'x')
          ? 'x'
          : provider === 'reddit'
            ? 'reddit'
            : 'custom';

    try {
      window.location.assign(`${this.backendUrl}/auth/${providerPath}/login`);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }

  async submitCredentials(): Promise<void> {
    if (!this.isBrowser || this.isSubmitting) {
      return;
    }

    const email = this.email.trim();
    const password = this.password.trim();

    if (!email || !password) {
      const msg = this.languageService.t('enterBothEmailPassword') || 'Enter both email and password.';
      this.errorMessage = msg;
      this.toast.error(this.languageService.t('validationError') || 'Required Fields', msg);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Attempt to obtain device FCM push token (non-blocking)
    let fcmToken: string | null = null;
    try {
      fcmToken = await this.fcmService.getFcmToken();
    } catch (tokenErr) {
      console.warn('[Login] FCM token acquisition skipped:', tokenErr);
    }

    const endpoint = this.isAdminMode ? '/auth/admin/login' : '/auth/user/login';
    const payload: Record<string, unknown> = { email, password };
    if (fcmToken) {
      payload['fcm_token'] = fcmToken;
      payload['device_type'] = 'web';
    }

    this.http.post<unknown>(`${this.backendUrl}${endpoint}`, payload)
      .pipe(finalize(() => {
        this.isSubmitting = false;
      }))
      .subscribe({
        next: (response) => {
          const token = this.extractToken(response);
          if (!token) {
            this.errorMessage = 'Login succeeded but no token was returned.';
            this.toast.error('Login Failed', this.errorMessage);
            return;
          }

          this.persistToken(token);
          this.email = '';
          this.password = '';

          const redirect = this.route.snapshot.queryParamMap.get('redirect');
          const fallback = this.isAdminMode ? '/admin/quiz' : '/';
          const target = redirect || fallback;

          // Use full-page redirect to ensure navigation works
          window.location.href = target;
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = this.extractErrorMessage(error);
          this.toast.error(this.isAdminMode ? 'Admin Login Failed' : 'Login Failed', this.errorMessage);
        }
      });
  }

  submitSignup(): void {
    if (!this.isBrowser || this.isSubmitting) {
      return;
    }

    const email = this.email.trim();
    const password = this.password.trim();
    const username = this.username.trim();

    if (!email || !password || !username) {
      const msg = this.languageService.t('emailUserPassRequired') || 'Email, username, and password are required.';
      this.errorMessage = msg;
      this.toast.error(this.languageService.t('validationError') || 'Required Fields', msg);
      return;
    }

    if (username.length < 3) {
      const msg = 'Username must be at least 3 characters.';
      this.errorMessage = msg;
      this.toast.error(this.languageService.t('validationError') || 'Validation Error', msg);
      return;
    }

    if (username.length > 30) {
      const msg = 'Username cannot exceed 30 characters.';
      this.errorMessage = msg;
      this.toast.error(this.languageService.t('validationError') || 'Validation Error', msg);
      return;
    }

    if (!/^[a-zA-Z0-9_.-]+$/.test(username)) {
      const msg = 'Username may only contain letters, numbers, underscores, dots, and hyphens.';
      this.errorMessage = msg;
      this.toast.error(this.languageService.t('validationError') || 'Validation Error', msg);
      return;
    }

    if (this.usernameStatus === 'taken') {
      const msg = 'This username is already taken. Please choose another for your profile.';
      this.errorMessage = msg;
      this.toast.error('Username Taken', msg);
      return;
    }

    if (password.length < 8) {
      const msg = this.languageService.t('passwordMinLength') || 'Password must be at least 8 characters.';
      this.errorMessage = msg;
      this.toast.error(this.languageService.t('validationError') || 'Validation Error', msg);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const payload: Record<string, unknown> = {
      email,
      username,
      password,
    };

    this.http.post<unknown>(`${this.backendUrl}/auth/user/register`, payload)
      .pipe(finalize(() => {
        this.isSubmitting = false;
      }))
      .subscribe({
        next: async () => {
          this.successMessage = 'Account created! Signing you in...';
          this.toast.success('Account Created', this.successMessage);

          let fcmToken: string | null = null;
          try {
            fcmToken = await this.fcmService.getFcmToken();
          } catch {
            // Non-blocking
          }

          const loginPayload: Record<string, unknown> = { email, password };
          if (fcmToken) {
            loginPayload['fcm_token'] = fcmToken;
            loginPayload['device_type'] = 'web';
          }

          // Auto-login after successful signup
          this.http.post<unknown>(`${this.backendUrl}/auth/user/login`, loginPayload)
            .subscribe({
              next: (loginResponse) => {
                const token = this.extractToken(loginResponse);
                if (token) {
                  this.persistToken(token);
                  const redirect = this.route.snapshot.queryParamMap.get('redirect');
                  const target = redirect || '/';
                  window.location.href = target;
                } else {
                  this.isLoginMode = true;
                  this.errorMessage = '';
                  this.successMessage = 'Account created. Please sign in.';
                }
              },
              error: () => {
                this.isLoginMode = true;
                this.errorMessage = '';
                this.successMessage = 'Account created. Please sign in.';
              }
            });
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = this.extractErrorMessage(error);
          if (this.errorMessage.toLowerCase().includes('username')) {
            this.usernameStatus = 'taken';
            this.usernameFeedback = this.errorMessage;
            this.toast.error('Username Taken', this.errorMessage);
          } else {
            this.toast.error('Registration Failed', this.errorMessage);
          }
        }
      });
  }

  get isAdminMode(): boolean {
    return this.authMode === 'admin';
  }

  private persistToken(token: string): void {
    const normalized = token.trim();
    if (!normalized) {
      return;
    }

    const isSecure = location.protocol === 'https:';
    this.cookieService.set('auth_token', normalized, {
      expires: 7,
      path: '/',
      secure: isSecure,
      sameSite: 'Lax'
    });
    setSessionAuthToken(normalized);
  }

  private extractToken(response: unknown): string {
    const record = this.asRecord(response);
    const nestedData = this.asRecord(record['data']);

    const candidates = [
      record['token'],
      record['access_token'],
      record['session_token'],
      nestedData['token'],
      nestedData['access_token'],
      nestedData['session_token']
    ];

    const token = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return typeof token === 'string' ? token.trim() : '';
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const payload = this.asRecord(error.error);
    const nestedData = this.asRecord(payload['data']);

    // Parse FastAPI detail (string or array of field validation errors)
    let detailMsg = '';
    const detail = payload['detail'];
    if (typeof detail === 'string') {
      detailMsg = detail.trim();
    } else if (Array.isArray(detail) && detail.length > 0) {
      const messages = detail.map((item) => {
        if (typeof item === 'object' && item !== null) {
          const record = item as Record<string, unknown>;
          const msg = String(record['msg'] || '').replace(/^Value error,\s*/i, '');
          const loc = Array.isArray(record['loc']) ? record['loc'] : [];
          const field = loc.length > 1 ? String(loc[loc.length - 1]) : '';
          return field ? `${field}: ${msg}` : msg;
        }
        return String(item);
      });
      detailMsg = messages.filter(Boolean).join(' • ');
    }

    const message =
      detailMsg ||
      this.readText(payload['message']) ||
      this.readText(payload['error']) ||
      this.readText(nestedData['message']);

    return message || (this.isLoginMode ? 'Unable to log in right now.' : 'Unable to sign up right now. Please check your details.');
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private readText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
